import { type Reference, gql, useApolloClient } from '@apollo/client'

import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { STORY_TYPES } from '@/constants/STORY_TYPES.ts'
import { useProjectId } from '@/hooks/useProjectId.ts'
import { addCacheListElement } from '@/requests/apollo/cache.ts'
import { ITERATION_GQL_SCHEMA } from '@/requests/apollo/schema.ts'
import { useQueryCurrentIteration, useQueryIterations, useQueryStories } from '@/requests/queries/useQueryProject.ts'
import { ONE_WEEK } from '@/utils/date.ts'

export const useGroupRelevantStories = () => {
  // biome-ignore lint/style/noNonNullAssertion:
  const projectId = useProjectId()!
  const { stories } = useQueryStories('relevant')
  const { currentIteration } = useQueryCurrentIteration()
  const { iterations } = useQueryIterations()
  const { cache } = useApolloClient()

  let currentVelocity = currentIteration.velocity
  if (currentIteration.number > 3) {
    const lastThreeIterationEffectivePoints = iterations
      .filter((iteration) => iteration.number < currentIteration.number)
      .slice(-3)
      .reduce((acc, iteration) => acc + (iteration.effectivePoints ?? 0), 0)

    if (lastThreeIterationEffectivePoints > 0) {
      currentVelocity = Math.max(1, Math.floor(lastThreeIterationEffectivePoints / 3))
    } else {
      currentVelocity = 10
    }
  }

  const initialIteration = { ...currentIteration, velocity: currentVelocity }
  const result: IterationWithStories[] = [
    {
      ...initialIteration,
      stories: [],
    },
  ]
  let accumulatedEstimate = 0
  let currentCapacity = initialIteration.velocity * initialIteration.teamStrength
  let acceptedPoints = 0

  const pushIteration = () => {
    const nextIterationNumber = result[result.length - 1].number + 1
    let nextIteration = iterations.find((iteration) => iteration.number === nextIterationNumber)
    if (!nextIteration) {
      const lastIteration = result[result.length - 1]
      const newIteration = {
        ...lastIteration,
        number: lastIteration.number + 1,
        start: lastIteration.start + ONE_WEEK,
        finish: lastIteration.finish + ONE_WEEK,
        teamStrength: 1,
      }
      addCacheListElement(cache.identify({ __typename: 'Project', id: projectId }), newIteration, 'iterations')
      nextIteration = newIteration
    }
    if (!nextIteration) throw new Error('Failed to create next iteration')
    result.push({
      ...nextIteration,
      velocity: 0,
      stories: [],
    } as IterationWithStories)
  }

  const pushStory = (story: Story) => result[result.length - 1].stories.push(story)
  const setIterationVelocity = (velocity: number) => {
    result[result.length - 1].velocity = velocity
  }

  for (const story of stories) {
    const estimate = story.estimate === -1 ? 0 : story.estimate

    switch (true) {
      case STORY_TYPES.RELEASE === story.storyType:
      case [
        STORY_STATUSES.STARTED,
        STORY_STATUSES.FINISHED,
        STORY_STATUSES.DELIVERED,
        STORY_STATUSES.ACCEPTED,
        STORY_STATUSES.REJECTED,
      ].includes(story.status): {
        if (STORY_STATUSES.ACCEPTED === story.status) acceptedPoints += estimate
        pushStory(story)
        accumulatedEstimate += estimate
        currentCapacity -= estimate
        break
      }
      case estimate > currentCapacity: {
        setIterationVelocity(accumulatedEstimate)
        while (currentCapacity < estimate) {
          pushIteration()
          currentCapacity =
            initialIteration.velocity * result[result.length - 1].teamStrength + Math.max(0, currentCapacity)
        }
        pushStory(story)
        currentCapacity -= estimate
        accumulatedEstimate = estimate
        break
      }
      default:
        accumulatedEstimate += estimate
        currentCapacity -= estimate
        pushStory(story)
    }
  }

  result[result.length - 1].velocity = accumulatedEstimate

  useCleanupUnusedIterations(projectId, result[result.length - 1].number + 1)

  result[0].acceptedPoints = acceptedPoints

  return result
}

const useCleanupUnusedIterations = (projectId: string, iterationNumber: number) => {
  const { cache } = useApolloClient()

  const getIteration = (projectId: string, iterationNumber: number) =>
    cache.readFragment<Iteration>({
      id: cache.identify({ __typename: 'Iteration', projectId, number: iterationNumber }),
      fragment: gql`fragment IterationFields on Iteration {
        ${ITERATION_GQL_SCHEMA}
      }`,
    })

  return () => {
    let iteration = getIteration(projectId, iterationNumber)
    for (let i = iterationNumber; iteration !== null; i++) {
      if (!iteration) break
      cache.modify({
        id: cache.identify({ __typename: 'Project', id: projectId }),
        fields: {
          // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
          iterations: (existingEntityRefs = [], { readField }) =>
            (existingEntityRefs as Reference[]).filter((entityRef: Reference) => readField('number', entityRef) !== i),
        },
      })
      cache.evict({ id: cache.identify({ __typename: 'Iteration', number: i, projectId }) })
      iteration = getIteration(projectId, iterationNumber + 1)
    }
  }
}
